// downloadHelper.ts
import { utils, writeFile } from "xlsx";
import toast from "react-hot-toast";

interface DownloadHelperParams<T> {
  selectedRows: string[];
  allSelectedData: T[];
  setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
  setAllSelectedData: React.Dispatch<React.SetStateAction<T[]>>;
  showCheckboxes: boolean;
  setShowCheckboxes: React.Dispatch<React.SetStateAction<boolean>>;
  setColumnsData: React.Dispatch<
    React.SetStateAction<{ text: string; value: string; isShow: boolean }[]>
  >;
  mapDataToExport: (data: T, index: number) => Record<string, any>;
  fileName: string;
}

export const handleDownload = <T>({
  selectedRows,
  allSelectedData,
  setSelectedRows,
  setAllSelectedData,
  showCheckboxes,
  setShowCheckboxes,
  setColumnsData,
  mapDataToExport,
  fileName,
}: DownloadHelperParams<T>) => {
  if (!showCheckboxes) {
    // Show checkboxes if not already visible
    setShowCheckboxes(true);
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === "select" ? { ...col, isShow: true } : col
      )
    );
    return;
  }

  // Check if any rows are selected
  if (selectedRows.length === 0) {
    toast.error("Please select at least one row to download");
    return;
  }

  const dataToExport = allSelectedData;
  if (!dataToExport.length) {
    toast.error("No data to export");
    return;
  }

  const exportData = dataToExport.map((item, index) =>
    mapDataToExport(item, index)
  );

  const createWorkbook = (styled = true) => {
    const worksheet = utils.json_to_sheet(exportData);
    worksheet["!cols"] = Object.keys(exportData[0]).map(() => ({ wch: 20 })); // Dynamic column widths
    worksheet["!rows"] = [{ hpt: 25 }, ...exportData.map(() => ({ hpt: 20 }))];

    if (styled) {
      const range = utils.decode_range(worksheet["!ref"] || "A1:Z1");

      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell = utils.encode_cell({ r: 0, c: C });
        if (!worksheet[cell]) continue;
        worksheet[cell].s = {
          font: {
            name: "Arial",
            sz: 14,
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          fill: { patternType: "solid", fgColor: { rgb: "4A90E2" } },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          border: {
            top: { style: "medium", color: { rgb: "000000" } },
            bottom: { style: "medium", color: { rgb: "000000" } },
            left: { style: "medium", color: { rgb: "000000" } },
            right: { style: "medium", color: { rgb: "000000" } },
          },
        };
      }

      for (let R = 1; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = utils.encode_cell({ r: R, c: C });
          if (!worksheet[cell]) continue;
          worksheet[cell].s = {
            font: { name: "Arial", sz: 12, color: { rgb: "000000" } },
            alignment: {
              horizontal: "left",
              vertical: "center",
              wrapText: true,
            },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
      }
    }

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Data");
    writeFile(workbook, fileName);
  };

  try {
    createWorkbook(true);
  } catch (error) {
    console.error("Styled export failed, exporting basic:", error);
    toast.error("Failed to apply styling. Using basic export.");
    createWorkbook(false);
  }

  setSelectedRows([]);
  setAllSelectedData([]);
  setShowCheckboxes(false);
  setColumnsData((prev) =>
    prev.map((col) =>
      col.value === "select" ? { ...col, isShow: false } : col
    )
  );
};
